// DishModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faArrowUp, faArrowDown, faCalculator } from '@fortawesome/free-solid-svg-icons';
import AutocompleteSelect from '../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';
import useProductAutocomplete from '../../../../components/ProductAutocomplete/useProductAutocomplete';
import MediaUploader from '../../../../components/ReferenceMedia/MediaUploader';

const categories = ['Перші страви', 'Другі страви', 'Каші', 'Гарніри', 'Салати', 'Десерти', 'Напої'];
const complexityOptions = ['Легка', 'Середня', 'Складна'];
const ageGroupOptions = ['1-3 роки', '3-6 років', '6-10 років'];

const emptyForm = {
  name: '', category: '', cookingTime: '', complexity: '', portionWeight: '',
  calories: '', proteins: '', fats: '', carbohydrates: '', allergens: '',
  publicDescription: '', ageGroups: []
};

const emptyIngredientRow = { baseProductId: '', baseProductName: '', unit: '', grossWeight: '', netWeight: '' };
const emptyStep = { description: '', duration: '', temperature: '', isCriticalControlPoint: false };

// Converts a netWeight (in the product's own stock unit) to grams/ml, since nutrition values on
// a base product are always "per 100г/мл" (see ProductModal). 'шт' (countable) can't be
// converted — auto-calc skips such ingredients and flags that it did.
const toGramsOrMl = (value, unit) => {
  const n = Number(value);
  if (!n || isNaN(n)) return null;
  if (unit === 'кг' || unit === 'л') return n * 1000;
  if (unit === 'г' || unit === 'мл') return n;
  return null;
};

const DishModal = ({
  isOpen, onClose, itemData, onSubmit, viewMode = false, isEditPossible = true, loading = false
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [ingredients, setIngredients] = useState([]);
  const [steps, setSteps] = useState([]);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const productSearch = useProductAutocomplete({ referenceType: 'products' });

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    productSearch.loadInitial();

    if (itemData) {
      setFormData({
        name: itemData.name || '',
        category: itemData.category || '',
        cookingTime: itemData.cookingTime || '',
        complexity: itemData.complexity || '',
        portionWeight: itemData.portionWeight || '',
        calories: itemData.calories || '',
        proteins: itemData.proteins || '',
        fats: itemData.fats || '',
        carbohydrates: itemData.carbohydrates || '',
        allergens: itemData.allergens || '',
        publicDescription: itemData.publicDescription || '',
        ageGroups: Array.isArray(itemData.ageGroups) ? itemData.ageGroups : []
      });
      setIngredients(Array.isArray(itemData.ingredients) && itemData.ingredients.every(i => typeof i === 'object')
        ? itemData.ingredients
        : []);
      setSteps(Array.isArray(itemData.steps) ? itemData.steps : []);
    } else {
      setFormData(emptyForm);
      setIngredients([]);
      setSteps([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemData]);

  // Backfill: older/seeded ingredient rows may only have baseProductId (no baseProductName/unit
  // snapshot saved at the time). Once the base-products list has loaded, resolve any missing
  // names/units by id so the table shows a name instead of a raw id.
  useEffect(() => {
    if (productSearch.options.length === 0) return;
    setIngredients(prev => {
      let changed = false;
      const next = prev.map(row => {
        if (row.baseProductName || !row.baseProductId) return row;
        const product = productSearch.options.find(p => p._id === row.baseProductId);
        if (!product) return row;
        changed = true;
        return { ...row, baseProductName: product.name, unit: row.unit || product.unit };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch.options]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleAgeGroup = (group) => {
    setFormData(prev => ({
      ...prev,
      ageGroups: prev.ageGroups.includes(group)
        ? prev.ageGroups.filter(g => g !== group)
        : [...prev.ageGroups, group]
    }));
  };

  // ---- Ingredients ----

  const addIngredientRow = () => setIngredients(prev => [...prev, { ...emptyIngredientRow }]);
  const removeIngredientRow = (index) => setIngredients(prev => prev.filter((_, i) => i !== index));

  const updateIngredientProduct = (index, productId) => {
    const product = productSearch.options.find(p => p._id === productId);
    setIngredients(prev => prev.map((row, i) => i === index ? {
      ...row,
      baseProductId: productId,
      baseProductName: product ? product.name : row.baseProductName,
      unit: product ? product.unit : row.unit
    } : row));
  };

  const updateIngredientField = (index, field, value) => {
    setIngredients(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  // ---- Auto-calculated КБЖУ from ingredients (per portion — netWeight values are assumed to
  // already be the amounts used for ONE portion, matching portionWeight's meaning). ----

  const computeNutrition = () => {
    let calories = 0, proteins = 0, fats = 0, carbohydrates = 0, skippedCount = 0;

    ingredients.forEach((row) => {
      const product = productSearch.options.find(p => p._id === row.baseProductId);
      if (!product) { skippedCount++; return; }

      const grams = toGramsOrMl(row.netWeight, row.unit);
      if (grams === null) { skippedCount++; return; }

      const factor = grams / 100;
      calories += factor * (Number(product.calories) || 0);
      proteins += factor * (Number(product.proteins) || 0);
      fats += factor * (Number(product.fats) || 0);
      carbohydrates += factor * (Number(product.carbohydrates) || 0);
    });

    return {
      calories: Math.round(calories),
      proteins: Math.round(proteins * 10) / 10,
      fats: Math.round(fats * 10) / 10,
      carbohydrates: Math.round(carbohydrates * 10) / 10,
      skippedCount,
      hasData: ingredients.length > 0
    };
  };

  const nutritionPreview = computeNutrition();

  const applyComputedNutrition = () => {
    setFormData(prev => ({
      ...prev,
      calories: nutritionPreview.calories,
      proteins: nutritionPreview.proteins,
      fats: nutritionPreview.fats,
      carbohydrates: nutritionPreview.carbohydrates
    }));
  };

  // ---- Steps ----

  const addStep = () => setSteps(prev => [...prev, { ...emptyStep }]);
  const removeStep = (index) => setSteps(prev => prev.filter((_, i) => i !== index));
  const updateStep = (index, field, value) => {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };
  const moveStep = (index, direction) => {
    setSteps(prev => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name || !formData.category) {
      setError('Заповніть назву і категорію страви');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        ingredients: ingredients.filter(row => row.baseProductId),
        steps: steps.map((s, i) => ({ ...s, order: i + 1 }))
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = viewMode || !isEditPossible || loading || submitting;

  return (
    <Modal show={isOpen} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {viewMode ? 'Перегляд страви' : itemData ? 'Редагувати страву' : 'Додати нову страву'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Назва страви {!viewMode && '*'}</Form.Label>
                <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required={!viewMode} disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Категорія {!viewMode && '*'}</Form.Label>
                <Form.Select name="category" value={formData.category} onChange={handleChange} required={!viewMode} disabled={disabled}>
                  <option value="">Оберіть категорію</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Час приготування</Form.Label>
                <Form.Control type="text" name="cookingTime" value={formData.cookingTime} onChange={handleChange} placeholder="напр. 40 хв" disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Складність</Form.Label>
                <Form.Select name="complexity" value={formData.complexity} onChange={handleChange} disabled={disabled}>
                  <option value="">Оберіть складність</option>
                  {complexityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Вага порції (г)</Form.Label>
                <Form.Control type="number" name="portionWeight" value={formData.portionWeight} onChange={handleChange} disabled={disabled} />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Вікові групи</Form.Label>
            <div className="d-flex gap-3 flex-wrap">
              {ageGroupOptions.map(group => (
                <Form.Check
                  key={group}
                  type="checkbox"
                  label={group}
                  checked={formData.ageGroups.includes(group)}
                  onChange={() => !disabled && toggleAgeGroup(group)}
                  disabled={disabled}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Алергени</Form.Label>
            <Form.Control type="text" name="allergens" value={formData.allergens} onChange={handleChange} disabled={disabled} />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Опис для батьків</Form.Label>
            <Form.Control
              as="textarea" rows={2}
              name="publicDescription"
              value={formData.publicDescription}
              onChange={handleChange}
              placeholder="Короткий апетитний опис страви для публічного меню — не рецептура і не технологія приготування"
              disabled={disabled}
            />
            <Form.Text className="text-muted">
              Показується батькам у публічному меню замість технічного опису — інгредієнти й
              технологія приготування там не відображаються.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Фото та відео страви</Form.Label>
            <MediaUploader
              target="dish"
              targetId={itemData?._id}
              itemName={itemData?.name || formData.name}
              allowVideo
              maxCount={6}
              disabled={disabled}
            />
          </Form.Group>

          {/* Ingredients */}
          <h6 className="mb-2">Інгредієнти</h6>
          <Table size="sm" responsive className="align-middle">
            <thead>
              <tr>
                <th style={{ minWidth: '260px' }}>Базовий продукт</th>
                <th>Брутто</th>
                <th>Нетто</th>
                <th>Одиниця</th>
                {!disabled && <th></th>}
              </tr>
            </thead>
            <tbody>
              {ingredients.map((row, index) => (
                <tr key={index}>
                  <td>
                    {(() => {
                      const isEditingProduct = editingIngredientIndex === index || !row.baseProductId;
                      if (disabled) return row.baseProductName || row.baseProductId;
                      if (!isEditingProduct) {
                        return (
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <span>{row.baseProductName || row.baseProductId}</span>
                            <Button variant="link" size="sm" className="p-0" onClick={() => setEditingIngredientIndex(index)}>
                              Змінити
                            </Button>
                          </div>
                        );
                      }
                      return (
                        <AutocompleteSelect
                          value={row.baseProductId}
                          onChange={(id) => { updateIngredientProduct(index, id); setEditingIngredientIndex(null); }}
                          options={productSearch.options}
                          loading={productSearch.loading}
                          loadingMore={productSearch.loadingMore}
                          hasMore={productSearch.hasMore}
                          onSearch={productSearch.search}
                          onLoadMore={productSearch.loadMore}
                          placeholder="Почніть вводити назву продукту..."
                        />
                      );
                    })()}
                  </td>
                  <td style={{ width: '110px' }}>
                    <Form.Control
                      type="number" step="0.001" min="0" size="sm"
                      value={row.grossWeight}
                      onChange={(e) => updateIngredientField(index, 'grossWeight', e.target.value)}
                      disabled={disabled}
                    />
                  </td>
                  <td style={{ width: '110px' }}>
                    <Form.Control
                      type="number" step="0.001" min="0" size="sm"
                      value={row.netWeight}
                      onChange={(e) => updateIngredientField(index, 'netWeight', e.target.value)}
                      disabled={disabled}
                    />
                  </td>
                  <td className="text-muted">{row.unit || '—'}</td>
                  {!disabled && (
                    <td>
                      <Button variant="link" className="text-danger p-0" onClick={() => removeIngredientRow(index)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {ingredients.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-3">Інгредієнти не додані</td></tr>
              )}
            </tbody>
          </Table>
          {!disabled && (
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={addIngredientRow}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Додати інгредієнт
            </Button>
          )}

          {nutritionPreview.hasData && (
            <Alert variant="light" className="border d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <FontAwesomeIcon icon={faCalculator} className="me-2 text-muted" />
                Розраховано з інгредієнтів (на порцію): <strong>{nutritionPreview.calories} ккал</strong>,
                {' '}Б {nutritionPreview.proteins} г, Ж {nutritionPreview.fats} г, В {nutritionPreview.carbohydrates} г
                {nutritionPreview.skippedCount > 0 && (
                  <Badge bg="warning" text="dark" className="ms-2">
                    не враховано {nutritionPreview.skippedCount} інгр. (штучні/без КБЖУ)
                  </Badge>
                )}
              </div>
              {!disabled && (
                <Button size="sm" variant="outline-primary" onClick={applyComputedNutrition}>
                  Застосувати до полів КБЖУ
                </Button>
              )}
            </Alert>
          )}

          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Калорії</Form.Label>
                <Form.Control type="number" name="calories" value={formData.calories} onChange={handleChange} disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Білки (г)</Form.Label>
                <Form.Control type="number" step="0.1" name="proteins" value={formData.proteins} onChange={handleChange} disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Жири (г)</Form.Label>
                <Form.Control type="number" step="0.1" name="fats" value={formData.fats} onChange={handleChange} disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Вуглеводи (г)</Form.Label>
                <Form.Control type="number" step="0.1" name="carbohydrates" value={formData.carbohydrates} onChange={handleChange} disabled={disabled} />
              </Form.Group>
            </Col>
          </Row>

          {/* Steps */}
          <h6 className="mb-2">Технологія приготування</h6>
          {steps.map((step, index) => (
            <div key={index} className="border rounded p-3 mb-2 bg-light">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <strong>Крок {index + 1}</strong>
                {!disabled && (
                  <div className="d-flex gap-1">
                    <Button variant="link" size="sm" className="p-0 text-muted" disabled={index === 0} onClick={() => moveStep(index, -1)}>
                      <FontAwesomeIcon icon={faArrowUp} />
                    </Button>
                    <Button variant="link" size="sm" className="p-0 text-muted" disabled={index === steps.length - 1} onClick={() => moveStep(index, 1)}>
                      <FontAwesomeIcon icon={faArrowDown} />
                    </Button>
                    <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeStep(index)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                )}
              </div>
              <Form.Group className="mb-2">
                <Form.Control
                  as="textarea" rows={2}
                  placeholder="Опис кроку"
                  value={step.description}
                  onChange={(e) => updateStep(index, 'description', e.target.value)}
                  disabled={disabled}
                />
              </Form.Group>
              <Row>
                <Col md={4}>
                  <Form.Control
                    type="text" size="sm" placeholder="Тривалість (напр. 10-15 хв)"
                    value={step.duration}
                    onChange={(e) => updateStep(index, 'duration', e.target.value)}
                    disabled={disabled}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    type="text" size="sm" placeholder="Температура (напр. 180°C)"
                    value={step.temperature}
                    onChange={(e) => updateStep(index, 'temperature', e.target.value)}
                    disabled={disabled}
                  />
                </Col>
                <Col md={4} className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    label="Критична точка контролю"
                    checked={!!step.isCriticalControlPoint}
                    onChange={(e) => updateStep(index, 'isCriticalControlPoint', e.target.checked)}
                    disabled={disabled}
                  />
                </Col>
              </Row>
            </div>
          ))}
          {steps.length === 0 && (
            <p className="text-muted small">Кроки приготування не додані.</p>
          )}
          {!disabled && (
            <Button variant="outline-secondary" size="sm" onClick={addStep}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Додати крок
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {viewMode ? 'Закрити' : 'Скасувати'}
          </Button>
          {!viewMode && isEditPossible && (
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting && <Spinner animation="border" size="sm" className="me-2" />}
              {itemData ? 'Зберегти зміни' : 'Додати страву'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DishModal;